import { inject, Injectable, signal } from "@angular/core";
import { CustomFormArray, CustomFormControl, CustomFormGroup } from "@tilby/tilby-ui-lib/components/tilby-magic-form";
import { ItemDefault, ItemFields } from "src/app/features/items/models/items-form.model";
import { Items, ItemsBomComponents, RawMaterials } from "tilby-models";
import { BomComponentsWithSubBomComponents, ItemBomComponentsFields, ItemBomComponentsFormArray, ItemStockFormGroups, ItemStockFormGroupsFields, ItemStockValidators, STOCK_KEY_FE } from "./item-stock.model";
import { ItemDataCatalogService } from "src/app/features/items/services/item-data-catalog.service";
import { EntityManagerService } from "src/app/core";
import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { MathUtils } from "@tilby/tilby-ui-lib/utilities";
import { TilbyCurrencyPipe } from "@tilby/tilby-ui-lib/pipes/tilby-currency";

export const RequireMinLength = (min: number = 1): ValidatorFn => {
    return (control: AbstractControl): ValidationErrors | null => {
      // Se il controllo è un FormArray, guardiamo la length, altrimenti value.length
      const length = control instanceof Array ? control.length : control.value?.length;

      if (length && length >= min) {
        return null; 
      }

      return { 'minArrayLength': { requiredLength: min, actualLength: length || 0 } };
    };
  }

@Injectable()
export class ItemStockFormService {

    private readonly itemDataCatalogService = inject(ItemDataCatalogService);
    private readonly entityManagerService = inject(EntityManagerService);
    private readonly tilbyCurrencyPipe = inject(TilbyCurrencyPipe);

    private itemStockForm!: ItemStockFormGroups;

    get form() {
        return this.itemStockForm;
    }

    bomItems: Array<Items> = [];
    bomRawMaterials: Array<RawMaterials> = [];

    bomItemsMap = new Map<number | string, any>();
    bomRawMaterialsMap = new Map<number | string, any>();
        
    initialBomComponents: Array<ItemBomComponentsFields> = [];
    stockFormReady = signal(false);

    validators: ItemStockValidators = {
        minQntAlert: 0,
        minBomComponentQnt: 0,

        // --------
        provMinLength: 0,
        provMaxLength: 255,
        provMaxLengthArray: new Array(10).fill(2)
    };

    createForm(item: ItemFields = new ItemDefault(), hiddenFields?: Array<keyof ItemFields>) {
        this.getBomItems(item?.id || null);
        this.getBomRawMaterials();

        this.initialBomComponents = item.bom_components || [];

        this.itemStockForm = new CustomFormGroup<ItemStockFormGroupsFields>({
            is_stock_active: new CustomFormControl(
                !!item.stock_type || false
            ),
            auto_unload: new CustomFormControl(
                item.auto_unload || false
            ),
            stock_type: new CustomFormControl(
                item.stock_type || null
            ),
            manual_production: new CustomFormControl(
                item.manual_production || false
            ),
            quantity_alert: new CustomFormControl(
                item.quantity_alert || null
            ),
            bom_components: new CustomFormArray<CustomFormGroup<BomComponentsWithSubBomComponents>>(
                []
            )
        })

        if (item.bom_components?.length) this.initComponent(item.bom_components);
        
        this.stockFormReady.set(true);
        return this.itemStockForm;
    }

    resetFormValues(item: Items = new ItemDefault()) {
        this.itemStockForm.patchValue({
            is_stock_active: !!item.stock_type || false,
            auto_unload: item.auto_unload || false,
            stock_type: item.stock_type || null,
            manual_production: item.manual_production || false,
            quantity_alert: item.quantity_alert || null
        });

        this.bomComponentsArray?.clear();

        if (item.bom_components?.length) this.initComponent(item.bom_components);

        this.itemStockForm.markAsPristine();
    }

    addNewComponents() {
        this.bomComponentsArray?.push(
            new CustomFormGroup<BomComponentsWithSubBomComponents>(
                {
                    type: new CustomFormControl(
                        'item'
                    ),
                    name: new CustomFormControl(
                        null,
                        { validators: [Validators.required] }
                    ),
                    component_item_id: new CustomFormControl(
                        null
                    ),
                    component_raw_material_id: new CustomFormControl(
                        null
                    ),
                    quantity: new CustomFormControl(
                        null,
                        { validators: [Validators.required, Validators.min(this.validators.minBomComponentQnt)] }
                    ),
                    cost: new CustomFormControl(
                        null
                    ),
                    unit: new CustomFormControl(
                        null
                    ),
                    bom_components: new CustomFormArray<CustomFormGroup<ItemBomComponentsFormArray>>(
                        []
                    ),
                    expanded: new CustomFormControl(
                        true
                    )
                }
            )
        )
    }

    removeComponent(componentIndex: number) {
        this.bomComponentsArray?.removeAt(componentIndex);
    }

    removeAllComponent() {
        for (let i = (this.bomComponentsArray?.value.length || 0) - 1; i >= 0; i--) {
            this.removeComponent(i);
        }
    }

    addSubComponent(componentIndex: number) {
        this.getSubComponentArray(componentIndex)?.push(new CustomFormGroup<ItemBomComponentsFormArray>({
            type: new CustomFormControl(
                'item'
            ),
            name: new CustomFormControl(
                null,
                { validators: [Validators.required] }
            ),
            component_item_id: new CustomFormControl(
                null
            ),
            component_raw_material_id: new CustomFormControl(
                null
            ),
            quantity: new CustomFormControl(
                null,
                { validators: [Validators.required, Validators.min(this.validators.minBomComponentQnt)] }
            ),
            cost: new CustomFormControl(
                null
            ),
            unit: new CustomFormControl(
                null
            ),
            expanded: new CustomFormControl(true)
        }))
    }

    removeSubComponent(componentIndex: number, subComponentIndex: number) {
        this.getSubComponentArray(componentIndex)?.removeAt(subComponentIndex);
    }

    removeAllSubComponent(componentIndex: number) {
        for (let i = (this.getSubComponentArray(componentIndex)?.value.length || 0) - 1; i >= 0; i--) {
            this.removeSubComponent(componentIndex, i);
        }
    }

    private getBomItems(currentItemId: number | null) {
        const items = this.itemDataCatalogService.getDataByKey("items") || [];

        const filteredItems: Items[] | undefined = items?.filter((item: Items) => {
            const hasNoCombinations = item.combinations?.length === 0;
            const isSimpleOrManualBom = item.stock_type === 'simple' || (item.stock_type === 'bom' && item.manual_production);
            const isNotCurrentItem = item.id !== currentItemId;
            return hasNoCombinations && isSimpleOrManualBom && isNotCurrentItem;
        });

        this.bomItems = filteredItems?.sort((a: Items, b: Items) => {
            return a.name.localeCompare(b.name);
        }) || [];

        this.bomItemsMap = new Map(this.bomItems.map(i => [i.id as number, i]))
    }

    private async getBomRawMaterials() {
        const rawMaterials = await this.entityManagerService.rawMaterials.fetchCollectionOffline({ stock_type: 'simple' });

        this.bomRawMaterials = rawMaterials?.sort((a: RawMaterials, b: RawMaterials) => {
            return a.name.localeCompare(b.name);
        }) || [];

        this.bomRawMaterialsMap = new Map(this.bomRawMaterials.map(r => [r.id as number, r]))
    }

    private initComponent(components: ItemBomComponentsFields[]) {
        components.forEach((component, index) => {
            const linkedComponent = component.component_item_id 
                ? this.bomItemsMap.get(component.component_item_id)
                : component.component_raw_material_id 
                ? this.bomRawMaterialsMap.get(component.component_raw_material_id)
                : null;

            this.bomComponentsArray?.push(new CustomFormGroup<BomComponentsWithSubBomComponents>({
                type: new CustomFormControl(
                    this.parseComponentType(component)
                ),
                id: new CustomFormControl(
                    component.id || null
                ),
                name: new CustomFormControl(
                    component.name || null,
                    { validators: [Validators.required] }
                ),
                component_item_id: new CustomFormControl(
                    component.component_item_id || null
                ),
                component_raw_material_id: new CustomFormControl(
                    component.component_raw_material_id || null
                ),
                quantity: new CustomFormControl(
                    component.quantity || null,
                    { validators: [Validators.required, Validators.min(this.validators.minBomComponentQnt)] }
                ),
                cost: new CustomFormControl(
                    linkedComponent?.cost
                        ? this.tilbyCurrencyPipe.transform(linkedComponent.cost)
                        : null
                ),
                unit: new CustomFormControl(
                    linkedComponent?.unit || null
                ),
                bom_components: new CustomFormArray<CustomFormGroup<ItemBomComponentsFormArray>>(
                    []
                ),
                expanded: new CustomFormControl(false)
            }))

            if(this.getTypeComponent(index)?.value === 'bom_component') {
                this.getNameComponent(index)?.setValidators(Validators.required);
            }

            if (component.bom_components?.length) {
                this.initSubComponent(component.bom_components, this.bomComponentsArray?.length ? this.bomComponentsArray.length - 1 : 0);
            }
        })
    }

    private initSubComponent(subComponents: Partial<ItemsBomComponents>[], componentIndex: number) {
        subComponents.forEach((subComponent) => {
            const linkedComponent = subComponent.component_item_id 
                ? this.bomItemsMap.get(subComponent.component_item_id)
                : subComponent.component_raw_material_id 
                ? this.bomRawMaterialsMap.get(subComponent.component_raw_material_id)
                : null;

            this.getSubComponentArray(componentIndex)?.push(new CustomFormGroup<ItemBomComponentsFormArray>({
                type: new CustomFormControl(
                    this.parseComponentType(subComponent)
                ),
                id: new CustomFormControl(
                    subComponent.id || null
                ),
                name: new CustomFormControl(
                    subComponent.name || null,
                    { validators: [Validators.required] }
                ),
                component_item_id: new CustomFormControl(
                    subComponent.component_item_id || null
                ),
                component_raw_material_id: new CustomFormControl(
                    subComponent.component_raw_material_id || null
                ),
                quantity: new CustomFormControl(
                    subComponent.quantity || null,
                    { validators: [Validators.required, Validators.min(this.validators.minBomComponentQnt)] }
                ),
                cost: new CustomFormControl(
                    linkedComponent?.cost
                        ? this.tilbyCurrencyPipe.transform(linkedComponent.cost)
                        : null
                ),
                unit: new CustomFormControl(
                    linkedComponent?.unit || null
                ),
                expanded: new CustomFormControl(false)
            }))
        })
    }

    parseComponentType(component: Partial<ItemsBomComponents>) {
        if (component.component_raw_material_id) {
            return 'raw_material';
        } else if (component.component_item_id) {
            return 'item';
        }
        return 'bom_component';
    }

    calculateItemCost(bomComponents: ItemBomComponentsFields[]) {
        if (!bomComponents || bomComponents.length === 0) return 0;

        return bomComponents.reduce((amount: number, bomComponent: any) => {
            let costToUse: number | undefined;

            if (bomComponent.component_item_id) {
                costToUse = this.bomItemsMap.get(bomComponent.component_item_id)?.cost
            } else if (bomComponent.component_raw_material_id) {
                costToUse = this.bomRawMaterialsMap.get(bomComponent.component_raw_material_id)?.cost;
            } else if (bomComponent.bom_components && bomComponent.bom_components.length) {
                // Chiamata ricorsiva se il componente ha dei sotto-componenti
                costToUse = this.calculateItemCost(bomComponent.bom_components);
            } else {
                // Se nessuna delle condizioni precedenti è soddisfatta, non c'è costo da aggiungere per questo componente
                return amount;
            }

            // Se costToUse è un numero valido moltiplico per la quantità del componente e sommo al costo totale
            if (typeof costToUse === 'number' && Number.isFinite(costToUse)) {
                const componentCost = MathUtils.round(costToUse * bomComponent.quantity || 0);
                return MathUtils.round(amount + componentCost);
            }
            return amount;
        }, 0);
    }

    getStockFields(currentItem: Items) {
        // Clono l'oggetto di destinazione per non mutare l'originale
        const updatedItem = { ...currentItem }; 

        // Recupero tutte le chiavi (nomi dei campi) presenti nel form
        const itemStockFieldsToMap = Object.keys(this.itemStockForm.controls);

        // Ciclo su tutte le chiavi trovate nel form
        itemStockFieldsToMap.forEach((key: string) => {
            if (!STOCK_KEY_FE.includes(key)) {
                const control = this.itemStockForm.get(key);

                if(control) {
                    let value = control.value;

                    if (key === 'bom_components' && Array.isArray(value)) {
                        // Creazione mappa <ID: bom_components> per i componenti distinta base dell'item già salvati 
                        const initialCompsMap = new Map(this.initialBomComponents.map(v => [v.id, v]));

                        value = value.map((component: ItemBomComponentsFields) => {
                            // Trova il componente già salvato per l'item (o oggetto vuoto se non trovato)
                            const initial = component.id? initialCompsMap.get(component.id): undefined;

                            // Creazione mappa <ID: valore_variante> per i valori della variante già salvati
                            const initialUnderCompsMap = new Map((initial?.bom_components || []).map((iv: any) => [iv.id, iv]));

                            // Gestione SOTTOCOMPONENTI
                            const bom_components = (component.bom_components || []).map((bom_component: Partial<ItemsBomComponents>) => {
                                const initialBomComponent = (bom_component.id ? initialUnderCompsMap.get(bom_component.id) : undefined) || {};

                                const rawBomComponent: ItemsBomComponents = {
                                    ...initialBomComponent,
                                    component_item_id: bom_component.component_item_id,
                                    component_raw_material_id: bom_component.component_raw_material_id,
                                    name: bom_component.name,
                                    quantity: bom_component.quantity || 0
                                }
                                return rawBomComponent;
                            }) as ItemsBomComponents[];

                            const rawComponents: ItemBomComponentsFields = {
                                ...(initial || {}),
                                component_item_id: component.component_item_id,
                                component_raw_material_id: component.component_raw_material_id,
                                name: component.name,
                                quantity: component.quantity || 0,
                                bom_components
                            };

                            return rawComponents; 
                        });
                    }
                    
                    if(key === 'stock_type' && !this.is_stock_active?.value) {
                        value = null;
                    }

                    // Assegno il valore all'oggetto
                    (updatedItem as any)[key] = value
                }
            }
        })
        return updatedItem;
    }

    get is_stock_active() {
        return this.itemStockForm?.controls.is_stock_active;
    }

    get auto_unload() {
        return this.itemStockForm?.controls.auto_unload;
    }

    get stock_type() {
        return this.itemStockForm?.controls.stock_type;
    }

    get manual_production() {
        return this.itemStockForm?.controls.manual_production;
    }

    get quantity_alert() {
        return this.itemStockForm?.controls.quantity_alert;
    }

    get bomComponentsArray() {
        return this.itemStockForm?.controls.bom_components;
    }

    getTypeComponent(index: number) {
        return this.bomComponentsArray?.at(index).controls.type;
    }

    getIdComponent(index: number) {
        return this.bomComponentsArray?.at(index).controls.id;
    }

    getNameComponent(index: number) {
        return this.bomComponentsArray?.at(index).controls.name;
    }

    getItemIdComponent(index: number) {
        return this.bomComponentsArray?.at(index).controls.component_item_id;
    }

    getRawMaterialIdComponent(index: number) {
        return this.bomComponentsArray?.at(index).controls.component_raw_material_id;
    }

    getQuantityComponent(index: number) {
        return this.bomComponentsArray?.at(index).controls.quantity;
    }

    getCostComponent(index: number) {
        return this.bomComponentsArray?.at(index).controls.cost;
    }

    getUnitComponent(index: number) {
        return this.bomComponentsArray?.at(index).controls.unit;
    }

    getExpandedComponent(index: number) {
        return this.bomComponentsArray?.at(index).controls.expanded;
    }

    getSubComponentArray(index: number) {
        return this.bomComponentsArray?.at(index).controls.bom_components;
    }

    getTypeSubComponent(index: number, subComponentIndex: number) {
        return this.getSubComponentArray(index)?.at(subComponentIndex).controls.type;
    }

    getIdSubComponent(index: number, subComponentIndex: number) {
        return this.getSubComponentArray(index)?.at(subComponentIndex).controls.id;
    }

    getNameSubComponent(index: number, subComponentIndex: number) {
        return this.getSubComponentArray(index)?.at(subComponentIndex).controls.name;
    }

    getItemIdSubComponent(index: number, subComponentIndex: number) {
        return this.getSubComponentArray(index)?.at(subComponentIndex).controls.component_item_id;
    }

    getRawMaterialIdSubComponent(index: number, subComponentIndex: number) {
        return this.getSubComponentArray(index)?.at(subComponentIndex).controls.component_raw_material_id;
    }

    getQuantitySubComponent(index: number, subComponentIndex: number) {
        return this.getSubComponentArray(index)?.at(subComponentIndex).controls.quantity;
    }

    getCostSubComponent(index: number, subComponentIndex: number) {
        return this.getSubComponentArray(index)?.at(subComponentIndex).controls.cost;
    }

    getUnitSubComponent(index: number, subComponentIndex: number) {
        return this.getSubComponentArray(index)?.at(subComponentIndex).controls.unit;
    }

    getExpandedSubComponent(index: number, subComponentIndex: number) {
        return this.getSubComponentArray(index)?.at(subComponentIndex).controls.expanded;
    }
}